/**
 * RV body-style vocabulary. "travel trailer" and "5th wheel" are product
 * categories, not free-text tokens: once a visitor names one, results must be
 * limited to that body style (a fifth wheel is never a valid answer for
 * "travel trailer under 30k").
 */

export interface RvTypeDef {
  id: string;
  label: string;
  /** How a visitor may type it (longest phrases win). */
  query: string[];
  /** Matched against the indexed rvType/category text. */
  match: RegExp;
}

export const RV_TYPE_DEFS: RvTypeDef[] = [
  {
    id: "fifth-wheel",
    label: "Fifth wheel",
    query: ["fifth wheel", "fifth wheels", "5th wheel", "5th wheels", "fiver", "fifthwheel"],
    match: /fifth\s*wheel|5th\s*wheel/i,
  },
  {
    id: "toy-hauler",
    label: "Toy hauler",
    query: ["toy hauler", "toy haulers", "toyhauler"],
    match: /toy\s*hauler/i,
  },
  {
    id: "destination-trailer",
    label: "Destination trailer",
    query: ["destination trailer", "destination trailers", "park model"],
    match: /destination\s*trailer|park\s*model/i,
  },
  {
    id: "pop-up",
    label: "Folding pop-up camper",
    query: ["pop up camper", "pop-up camper", "popup camper", "pop up", "popup", "folding camper", "tent trailer"],
    match: /pop[\s-]?up|folding/i,
  },
  {
    id: "truck-camper",
    label: "Truck camper",
    query: ["truck camper", "truck campers", "slide in camper"],
    match: /truck\s*camper/i,
  },
  {
    id: "class-a",
    label: "Class A motorhome",
    query: ["class a motorhome", "class a motor home", "class a", "classa"],
    match: /class\s*a\b/i,
  },
  {
    id: "class-b",
    label: "Class B motorhome",
    query: ["class b motorhome", "class b motor home", "camper van", "class b", "classb"],
    match: /class\s*b\b/i,
  },
  {
    id: "class-c",
    label: "Class C motorhome",
    query: ["class c motorhome", "class c motor home", "class c", "classc"],
    match: /class\s*c\b/i,
  },
  {
    id: "motorhome",
    label: "Motorhome",
    query: ["motorhome", "motorhomes", "motor home", "motor homes", "coach", "rv motorhome"],
    match: /motor\s*home|motorhome|class\s*[abc]\b/i,
  },
  {
    id: "travel-trailer",
    label: "Travel trailer",
    // "travel trailer" last-ish so more specific styles claim the words first
    query: ["travel trailer", "travel trailers", "traveltrailer", "bumper pull"],
    match: /travel\s*trailer/i,
  },
];

const BY_ID = new Map(RV_TYPE_DEFS.map((d) => [d.id, d]));

export function rvTypeLabel(id: string): string {
  return BY_ID.get(id)?.label ?? id;
}

/** True when an indexed unit's rvType/category satisfies the requested style. */
export function rvTypeMatches(id: string, rvType?: string | null, category?: string | null): boolean {
  const def = BY_ID.get(id);
  if (!def) return true;
  const text = `${rvType ?? ""} ${category ?? ""}`;
  if (!text.trim()) return false;
  if (!def.match.test(text)) return false;
  // A destination trailer / toy hauler is its own style, not a plain travel trailer.
  if (id === "travel-trailer" && /destination\s*trailer|toy\s*hauler|park\s*model/i.test(text)) return false;
  return true;
}

/**
 * Pull a body style out of a visitor query and remove the wording so it never
 * becomes a required title token ("trailer" alone matched everything before).
 */
export function detectRvType(text: string): { id: string | null; phrase: string | null; text: string } {
  let out = ` ${text} `;
  const phrases = RV_TYPE_DEFS.flatMap((d) => d.query.map((q) => ({ id: d.id, q: q.toLowerCase() }))).sort(
    (a, b) => b.q.length - a.q.length,
  );
  for (const { id, q } of phrases) {
    const needle = ` ${q} `;
    if (out.includes(needle)) {
      out = out.split(needle).join(" ");
      return { id, phrase: q, text: out.replace(/\s+/g, " ").trim() };
    }
  }
  return { id: null, phrase: null, text };
}
